import React, { useCallback, useEffect, useRef, useState } from "react";
import { Badge, Button, Popover, PopoverSurface, PopoverTrigger, Body2, Spinner } from "@fluentui/react-components";
import { InfoRegular, WarningRegular } from "@fluentui/react-icons";
import { getYouTrackBreakdown } from "../../services/expectedHoursService";
import type { IYouTrackProjectHoursLine } from "../../services/types/expectedHours";
import appErrorToast from "../common/AppErrorToast";
import { payoutBreakdownStyles } from "./PayoutBreakdownInfoButtonStyles";
import { resolveDecisionBadgePresentation } from "./resourceAllocationRowPresentation";
import { formatResourceAllocationDuration } from "../../utils/resourceAllocationDuration";

interface YouTrackBreakdownInfoButtonProps {
  userId: string;
  year: number;
  month: number;
  ariaLabel: string;
}

const YouTrackBreakdownInfoButton: React.FC<YouTrackBreakdownInfoButtonProps> = ({
  userId,
  year,
  month,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [lines, setLines] = useState<IYouTrackProjectHoursLine[] | null>(null);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await getYouTrackBreakdown(userId, year, month);
      if (mounted.current) setLines(data);
    } catch {
      appErrorToast("Nie udało się pobrać szczegółów YouTrack.");
      if (mounted.current) setLines([]);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [userId, year, month]);

  useEffect(() => {
    if (!open) return;
    setLines(null);
    setLoading(true);
    void load();
  }, [open, load]);

  return (
    <Popover open={open} onOpenChange={(_, data) => setOpen(data.open || false)}>
      <PopoverTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          size="small"
          icon={<InfoRegular />}
          aria-label={ariaLabel}
        />
      </PopoverTrigger>
      <PopoverSurface tabIndex={-1} style={{ ...payoutBreakdownStyles.popoverSurface, maxWidth: 700 }}>
        <Body2 style={payoutBreakdownStyles.title}>YouTrack</Body2>
        {loading && (
          <div style={payoutBreakdownStyles.spinnerRow}>
            <Spinner size="small" />
          </div>
        )}
        {!loading && lines && lines.length === 0 && (
          <Body2>Brak pozycji do wyświetlenia.</Body2>
        )}
        {!loading && lines && lines.length > 0 && (
          <table style={payoutBreakdownStyles.table}>
            <thead>
              <tr>
                <th style={payoutBreakdownStyles.th}>Projekt</th>
                <th style={payoutBreakdownStyles.th}>Liczba godzin</th>
                <th style={payoutBreakdownStyles.th}>Zaakceptowane godziny</th>
                <th style={payoutBreakdownStyles.th}>Status rozliczenia</th>
                <th style={payoutBreakdownStyles.th}>Kierownik</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => {
                const badgePresentation = resolveDecisionBadgePresentation(line.settlementStatus);
                const currentMinutes = Math.round(line.hours * 60);
                const showWarning =
                  line.settlementStatus === "accepted" &&
                  line.acceptedYouTrackMinutes != null &&
                  currentMinutes !== line.acceptedYouTrackMinutes;
                return (
                  <tr key={`${line.projectName}-${index}`}>
                    <td style={payoutBreakdownStyles.td}>
                      {line.projectName}
                      {showWarning && (
                        <WarningRegular
                          style={{ color: "#d83b01", marginLeft: 4, verticalAlign: "middle" }}
                        />
                      )}
                    </td>
                    <td style={payoutBreakdownStyles.td}>{line.hours}</td>
                    <td style={payoutBreakdownStyles.td}>
                      {line.acceptedYouTrackMinutes != null
                        ? formatResourceAllocationDuration(line.acceptedYouTrackMinutes)
                        : "—"}
                    </td>
                    <td style={payoutBreakdownStyles.td}>
                      {badgePresentation ? (
                        <Badge
                          shape="rounded"
                          size="small"
                          appearance={badgePresentation.appearance}
                          color={badgePresentation.color}
                        >
                          {badgePresentation.label}
                        </Badge>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={payoutBreakdownStyles.td}>{line.managerFullName ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </PopoverSurface>
    </Popover>
  );
};

export default YouTrackBreakdownInfoButton;
